import { memo } from 'react';
import clsx from 'clsx';

interface CellProps {
  index: number;
  value: number;
  isFixed: boolean;
  isSelected: boolean;
  isRelated: boolean;
  isSameNumber: boolean;
  isError: boolean;
  notes: number[];
  onClick: () => void;
  gridSize?: number;
  boxSize?: number;
}

function Cell({
  index,
  value,
  isFixed,
  isSelected,
  isRelated,
  isSameNumber,
  isError,
  notes,
  onClick,
  gridSize = 9,
  boxSize = 3,
}: CellProps) {
  // Calculate border classes for box separation
  const row = Math.floor(index / gridSize);
  const col = index % gridSize;

  // Determine if this cell is at a box boundary
  const isRightBoxBorder = (col + 1) % boxSize === 0 && col < gridSize - 1;
  const isBottomBoxBorder = (row + 1) % boxSize === 0 && row < gridSize - 1;

  const borderClasses = clsx({
    'border-r-[3px] border-r-green-600': isRightBoxBorder,
    'border-b-[3px] border-b-green-600': isBottomBoxBorder,
  });

  const cellClasses = clsx(
    'sudoku-cell',
    borderClasses,
    {
      'selected': isSelected,
      'related': isRelated && !isSelected && !isSameNumber,
      'same-number': isSameNumber && !isSelected,
      'error': isError,
      'is-fixed': isFixed,
      'user-input': !isFixed && value !== 0,
    }
  );

  // For 4x4 grid, notes grid should be 2x2
  const notesGridClass = gridSize === 4 ? 'notes-grid-4' : 'notes-grid';
  const maxNumber = gridSize;

  return (
    <div className={cellClasses} onClick={onClick}>
      {value !== 0 && value}
      {value === 0 && notes.length > 0 && (
        <div className={notesGridClass}>
          {Array.from({ length: maxNumber }, (_, i) => i + 1).map((num) => (
            <span key={num} className="note-num">
              {notes.includes(num) ? num : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(Cell);
